import { supabase } from '@/lib/supabase';

export type NotificationType = 'message' | 'connection' | 'opportunity' | 'system';

export interface Notification {
  id: string;
  profile_id: string;
  type: NotificationType;
  title: string;
  description?: string;
  timestamp: string;
  read: boolean;
  action_url?: string;
  related_entity_id?: string;
  related_entity_reference?: string;
  related_entity_type?: string;
}

export interface CreateNotificationParams {
  profileId: string;
  type: NotificationType;
  title: string;
  description?: string;
  actionUrl?: string;
  // Either UUID reference or string reference should be provided, not both
  relatedEntityId?: string; // UUID format for database references
  relatedEntityReference?: string; // Text format for non-UUID references
  relatedEntityType?: string;
}

/**
 * Creates a new notification in the database
 */
export async function createNotification(params: CreateNotificationParams): Promise<string | null> {
  const { 
    profileId, 
    type, 
    title, 
    description, 
    actionUrl, 
    relatedEntityId, 
    relatedEntityReference,
    relatedEntityType 
  } = params;
  
  try {
    // Validate that the user exists first to avoid RLS policy violations
    const { data: userExists, error: userCheckError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', profileId)
      .single();
      
    if (userCheckError || !userExists) {
      console.error('Error creating notification: User not found or not authorized', userCheckError);
      return null;
    }
    
    // Create the notification
    const { data, error } = await supabase
      .from('notifications')
      .insert([
        {
          profile_id: profileId,
          type,
          title,
          description,
          action_url: actionUrl,
          related_entity_id: relatedEntityId, // UUID reference
          related_entity_reference: relatedEntityReference, // Text reference
          related_entity_type: relatedEntityType,
          timestamp: new Date().toISOString(),
          read: false
        }
      ])
      .select('id')
      .single();
      
    if (error) {
      console.error('Error creating notification:', error);
      return null;
    }
    
    return data.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
}

/**
 * Gets all notifications for a user
 */
export async function getNotifications(profileId: string): Promise<Notification[]> {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('profile_id', profileId)
      .order('timestamp', { ascending: false });
      
    if (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}

/**
 * Gets the count of unread notifications for a user
 */
export async function getUnreadNotificationsCount(profileId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', profileId)
      .eq('read', false);
      
    if (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
    
    return count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
}

/**
 * Marks a notification as read
 */
export async function markNotificationAsRead(notificationId: string, profileId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('profile_id', profileId);
      
    if (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }
}

/**
 * Marks all notifications as read for a user
 */
export async function markAllNotificationsAsRead(profileId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('profile_id', profileId)
      .eq('read', false);
      
    if (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return false;
  }
}

/**
 * Deletes a notification
 */
export async function deleteNotification(notificationId: string, profileId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('profile_id', profileId);
      
    if (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    return false;
  }
}